import type { Project } from '@shared/types'

/** Shown for a linked TN analysis whose run left no dashboard and no error (WI 2399). */
const TN_INTERRUPTED = 'The analysis did not finish (it may have been interrupted). Delete it and run the scenario again.'

/** The parts of a tracked generation job that decide whether a TN analysis failed. */
interface TnJobState {
  projectId: string
  status: 'pending' | 'running' | 'succeeded' | 'failed'
  error?: string
}

/**
 * Why a GCC-linked TN analysis failed, or null when it is ready, running or not yet known.
 * Shared by the GCC design's TN card and the GCC TN Analysis page, so both say the same
 * thing (WI 2497). `synced` is false until the first job poll, when a missing job means
 * nothing yet.
 */
export function gccTnFailure(
  p: Pick<Project, 'id' | 'data' | 'gccTnError'>,
  jobs: readonly TnJobState[],
  synced: boolean,
): string | null {
  if (p.data || jobs.some(j => j.projectId === p.id && (j.status === 'pending' || j.status === 'running'))) return null
  if (p.gccTnError) return p.gccTnError
  const tracked = jobs.find(j => j.projectId === p.id && j.status === 'failed')
  if (tracked) return tracked.error || TN_INTERRUPTED
  // No dashboard and no job once jobs are synced: the run was lost (e.g. a server restart).
  return synced ? TN_INTERRUPTED : null
}
